import { Component, ViewChild, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import { Router, RouterOutlet, NavigationEnd, ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ParameterService, Parameter } from '../../services/parameter.service';
import { LoggerService } from '../../core/services/logger.service';
import { catchError, finalize, filter } from 'rxjs/operators';
import { of, Subscription } from 'rxjs';
import { DeleteParameterDialogComponent } from './delete-parameter-dialog/delete-parameter-dialog.component';

@Component({
    selector: 'app-parameters-page',
    imports: [CommonModule, NgIf, RouterOutlet, MatCardModule, MatButtonModule, MatIconModule, MatMenuModule, MatPaginatorModule, MatTableModule, MatSortModule, MatSnackBarModule, MatDialogModule, MatTooltipModule],
    templateUrl: './parameters-page.component.html',
    styleUrls: ['./parameters-page.component.scss']
})
export class ParametersPageComponent implements OnInit, AfterViewInit, OnDestroy {
    displayedColumns: string[] = ['key', 'value', 'description', 'status', 'action'];
    dataSource = new MatTableDataSource<ParameterElement>([]);
    isLoading = false;
    private routerSubscription?: Subscription;

    @ViewChild(MatPaginator) paginator!: MatPaginator;
    @ViewChild(MatSort) sort!: MatSort;

    constructor(
        // eslint-disable-next-line no-unused-vars
        private parameterService: ParameterService,
        // eslint-disable-next-line no-unused-vars
        private snackBar: MatSnackBar,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService,
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private route: ActivatedRoute,
        // eslint-disable-next-line no-unused-vars
        private dialog: MatDialog,
        // eslint-disable-next-line no-unused-vars
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.loadParameters();
        this.subscribeToRouterEvents();
    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }

    private subscribeToRouterEvents(): void {
        this.routerSubscription = this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd)
            )
            .subscribe((event: NavigationEnd) => {
                const url = event.urlAfterRedirects;
                if (url === '/parameters' || url.startsWith('/parameters?')) {
                    this.logger.info('Navegación detectada a /parameters, refrescando lista de parámetros', 'ParametersPageComponent');
                    this.loadParameters();
                }
            });
    }

    ngAfterViewInit(): void {
        if (this.paginator) {
            this.dataSource.paginator = this.paginator;
        }
        if (this.sort) {
            this.dataSource.sort = this.sort;
        }
        this.dataSource.sortingDataAccessor = (item: ParameterElement, property: string): string => {
            switch (property) {
                case 'key':
                    return item.key;
                case 'value':
                    return item.value;
                case 'description':
                    return item.description || '';
                case 'status':
                    return item.status;
                default:
                    return (item as unknown as Record<string, string>)[property] ?? '';
            }
        };
    }

    loadParameters(): void {
        this.logger.info('Iniciando carga de parámetros desde el backend', 'ParametersPageComponent');
        this.isLoading = true;
        this.parameterService.getParameters()
            .pipe(
                catchError(error => {
                    this.logger.error('Error al cargar parámetros', error as Error, 'ParametersPageComponent');
                    this.snackBar.open('Error al cargar parámetros', 'Cerrar', { duration: 5000, panelClass: ['error-snackbar'] });
                    return of({ success: false, data: [] });
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe(response => {
                this.logger.info(`Respuesta del backend: ${JSON.stringify(response)}`, 'ParametersPageComponent');
                if (response.success && response.data) {
                    const parameterElements = response.data.map((parameter: Parameter) => this.mapParameterToParameterElement(parameter));
                    this.logger.info(`Mapeados ${parameterElements.length} parámetros para la tabla`, 'ParametersPageComponent');
                    
                    this.dataSource.data = parameterElements;
                    
                    if (this.paginator) {
                        this.dataSource.paginator = null;
                        this.dataSource.paginator = this.paginator;
                    }
                    
                    this.cdr.detectChanges();
                } else {
                    this.logger.warn('Respuesta del backend no exitosa o sin datos', 'ParametersPageComponent');
                }
            });
    }

    editParameter(parameterElement: ParameterElement): void {
        this.logger.info(`Editar parámetro: ${parameterElement.key}`, 'ParametersPageComponent');
        this.parameterService.getParameters()
            .pipe(
                catchError(error => {
                    this.logger.error('Error al obtener datos del parámetro', error as Error, 'ParametersPageComponent');
                    this.snackBar.open('Error al obtener datos del parámetro', 'Cerrar', { duration: 5000, panelClass: ['error-snackbar'] });
                    return of({ success: false, data: [] });
                })
            )
            .subscribe(response => {
                if (response.success && response.data) {
                    const parameter = response.data.find((p: Parameter) => p.key === parameterElement.key);
                    if (parameter) {
                        this.router.navigate(['/parameters/edit', parameter.id]);
                    } else {
                        this.snackBar.open('Parámetro no encontrado', 'Cerrar', { duration: 3000, panelClass: ['error-snackbar'] });
                    }
                }
            });
    }

    deleteParameter(parameterElement: ParameterElement): void {
        this.logger.info(`Eliminar parámetro: ${parameterElement.key}`, 'ParametersPageComponent');
        this.parameterService.getParameters()
            .pipe(
                catchError(error => {
                    this.logger.error('Error al obtener datos del parámetro', error as Error, 'ParametersPageComponent');
                    this.snackBar.open('Error al obtener datos del parámetro', 'Cerrar', { duration: 5000, panelClass: ['error-snackbar'] });
                    return of({ success: false, data: [] });
                })
            )
            .subscribe(response => {
                if (response.success && response.data) {
                    const parameter = response.data.find((p: Parameter) => p.key === parameterElement.key);
                    if (parameter) {
                        this.openDeleteConfirmationDialog(parameter);
                    } else {
                        this.snackBar.open('Parámetro no encontrado', 'Cerrar', { duration: 3000, panelClass: ['error-snackbar'] });
                    }
                }
            });
    }

    private openDeleteConfirmationDialog(parameter: Parameter): void {
        const dialogRef = this.dialog.open(DeleteParameterDialogComponent, {
            width: '500px',
            maxWidth: '90vw',
            disableClose: true,
            data: { parameter }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.performDeleteParameter(parameter);
            }
        });
    }

    private performDeleteParameter(parameter: Parameter): void {
        this.logger.info(`Iniciando eliminación del parámetro: ${parameter.key} (ID: ${parameter.id})`, 'ParametersPageComponent');
        this.isLoading = true;
        this.parameterService.deleteParameter(parameter.id)
            .pipe(
                catchError(error => {
                    this.logger.error('Error al eliminar parámetro', error as Error, 'ParametersPageComponent');
                    const errorMessage = (error as any).error?.message || 'Error al eliminar el parámetro';
                    this.snackBar.open(errorMessage, 'Cerrar', { duration: 5000, panelClass: ['error-snackbar'] });
                    this.isLoading = false;
                    return of(null);
                }),
                finalize(() => {
                    this.isLoading = false;
                })
            )
            .subscribe(response => {
                this.logger.info(`Respuesta de eliminación: ${JSON.stringify(response)}`, 'ParametersPageComponent');
                if (response && response.success) {
                    this.logger.info('Parámetro eliminado exitosamente, recargando lista', 'ParametersPageComponent');
                    this.loadParameters();
                    this.snackBar.open('Parámetro eliminado exitosamente', 'Cerrar', { duration: 3000, panelClass: ['success-snackbar'] });
                } else {
                    this.logger.warn('Respuesta de eliminación no exitosa', 'ParametersPageComponent');
                }
            });
    }

    onCreateParameter(): void {
        this.router.navigate(['/parameters/create']);
    }

    private mapParameterToParameterElement(parameter: Parameter): ParameterElement {
        return {
            key: parameter.key,
            value: parameter.value,
            description: parameter.description || '',
            status: parameter.isActive ? 'active' : 'inactive'
        };
    }

    isBaseRoute(): boolean {
        const url = this.router.url || '';
        return url === '/parameters' || url === '/parameters/' || url.startsWith('/parameters?');
    }

    isCreateRoute(): boolean {
        const url = this.router.url || '';
        return url.startsWith('/parameters/create');
    }
}

export interface ParameterElement {
    key: string;
    value: string;
    description: string;
    status: string;
}

